import { DescriptionsProps } from 'antd'
import { AxiosError } from 'axios'

import { client } from './client'
import * as ENDPOINTS from './endpoints'

import {
  getDisplayValue,
  GENDER_MAPPINGS,
  ACTIVITIES_MAPPINGS,
  USER_TYPE_MAPPINGS,
  CONNECT_WITH_MAPPINGS,
} from 'src/constants/mappings'
export interface ConnectionProfileType extends JSON {
  id: string
  activities: string[]
  otherActivitiesText?: string
  connectWith: string
  targetGender: string[]
  location?: string
  postcode: string
  range: number
  profile: number
}

export interface ProfileType extends JSON {
  id: string
  name: string
  gender?: string
  genderDescription?: string
  pronouns?: string
  dateOfBirth?: string
  email?: string
  bio: string
  userType: string
  avatar?: string
  notificationsEnabled?: boolean
  user: string
  connectProfiles: ConnectionProfileType
  // Organization-specific fields
  organization?: {
    id: number
    name: string
    url?: string
    avatar?: string
    userId?: string
    contactEmail?: string
    contactInfo?: string
    location?: string
  }
  organizationName?: string
  organizationWebsite?: string
  organizationContactEmail?: string
  organizationContactInfo?: string
  organizationLocation?: string
  // Verification status
  isVerified?: boolean
  // Pending connection status (populated by discovery endpoints)
  connectionStatus?: {
    status: 'requestSent' | 'requestReceived'
    connectionId: string
  } | null
}

// Type guard function
export const isOrganisationProfile = (
  profile: ProfileType
): profile is ProfileType & { userType: 'organisation' } => {
  return profile.userType === 'organisation'
}

export interface FilterProfiles {
  userType: string
  bio: string
  preferConnectWith: string
  preferConnectWithGender: string
  postcode: string
  activeRange: number
  activities: string
}

export interface ResponseFail {
  message?: string
  detail?: string
}

interface AvatarUploadResponse {
  message: string
  avatar_url: string
}

const FIELD_TO_MAPPING: Record<string, Record<string, string>> = {
  gender: GENDER_MAPPINGS,
  preferConnectWithGender: GENDER_MAPPINGS,
  activities: ACTIVITIES_MAPPINGS,
  userType: USER_TYPE_MAPPINGS,
  preferConnectWith: CONNECT_WITH_MAPPINGS,
}

const FIELD_LABELS: Record<string, string> = {
  userType: 'User Type',
  bio: 'Bio',
  preferConnectWith: 'Prefer Connect With',
  preferConnectWithGender: 'Prefer Connect With Gender',
  postcode: 'Postcode',
  activeRange: 'Active Range',
  activities: 'Activities',
}

export const transformToFilterProfiles = (profile: ProfileType | null): FilterProfiles => {
  if (!profile) {
    return {
      userType: '',
      bio: '',
      preferConnectWith: '',
      preferConnectWithGender: '',
      postcode: '',
      activeRange: 0,
      activities: '',
    }
  } else {
    return {
      userType: profile.userType,
      bio: profile.bio,
      preferConnectWith: profile.connectProfiles.connectWith,
      preferConnectWithGender: profile.connectProfiles.targetGender.join(', '),
      postcode: profile.connectProfiles.postcode,
      activeRange: profile.connectProfiles.range,
      activities: profile.connectProfiles.activities.join(', '),
    }
  }
}

export const generateDescriptionsItems = (
  filterProfile: FilterProfiles
): DescriptionsProps['items'] => {
  return Object.entries(filterProfile).map(([key, value]) => {
    const label =
      FIELD_LABELS[key] ||
      key.charAt(0).toUpperCase() + key.slice(1).replace(/([a-z])([A-Z])/g, '$1 $2')

    let formattedValue: string

    if (!value) {
      formattedValue = 'N/A'
    } else if (key === 'activities') {
      const activitiesList = (value as string).split(', ')
      formattedValue = activitiesList
        .map((activity) => getDisplayValue(activity, ACTIVITIES_MAPPINGS))
        .join(', ')
    } else if (FIELD_TO_MAPPING[key]) {
      if (typeof value === 'string' && value.includes(', ')) {
        formattedValue = value
          .split(', ')
          .map((v) => getDisplayValue(v, FIELD_TO_MAPPING[key]))
          .join(', ')
      } else {
        formattedValue = getDisplayValue(value as string, FIELD_TO_MAPPING[key])
      }
    } else if (typeof value === 'string') {
      formattedValue = value.replace(/_/g, ' ')
    } else {
      formattedValue = String(value)
    }

    return {
      key,
      label,
      children: formattedValue,
    }
  })
}

export const getOneUserProfile = async (userId: string): Promise<ProfileType | ResponseFail> => {
  try {
    const response = await client.get<ProfileType>(`${ENDPOINTS.GET_USER_PROFILE}${userId}`)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { detail?: string }
      if (data.detail) {
        return { message: data.detail }
      }
    }
    return { message: 'An unexpected error occurred. Please try again later.' }
  }
}

export const getSelfProfile = async (): Promise<ProfileType | ResponseFail> => {
  try {
    const response = await client.get<ProfileType>(ENDPOINTS.GET_SELF_PROFILE)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { detail?: string }
      if (data.detail) {
        return { message: data.detail }
      }
    }
    return { message: 'An unexpected error occurred. Please try again later.' }
  }
}

export const updateUserProfile = async (
  data: Partial<ProfileType>
): Promise<ProfileType | ResponseFail> => {
  try {
    const jsonSerializableData = data
    const response = await client.put<ProfileType, JSON>(
      ENDPOINTS.UPDATE_SELF_PROFILE,
      jsonSerializableData as JSON
    )
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseData = error.response.data as { detail?: string }
      if (responseData.detail) {
        return { message: responseData.detail }
      }
    }
    return { message: 'An unexpected error occurred. Please try again later.' }
  }
}

export const uploadAvatar = async (file: File): Promise<AvatarUploadResponse | ResponseFail> => {
  try {
    const response = await client.uploadFile<AvatarUploadResponse>(ENDPOINTS.UPLOAD_AVATAR, file)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      const responseData = error.response.data as { error?: string }
      if (responseData.error) {
        return { message: responseData.error }
      }
    }
    return { message: 'Avatar upload failed. Please try again later.' }
  }
}

export const deleteAccount = async (): Promise<{ message: string } | ResponseFail> => {
  try {
    const response = await client.delete<{ message: string }>(ENDPOINTS.DELETE_ACCOUNT)
    return response.data
  } catch (error) {
    if (error instanceof AxiosError && error.response?.data) {
      const data = error.response.data as { error?: string; detail?: string }
      if (data.error) {
        return { message: data.error }
      }
      if (data.detail) {
        return { message: data.detail }
      }
    }
    return { message: 'Failed to delete account. Please try again later.' }
  }
}
